<template>
  <BaseModal class="address-modal" v-model="isOpen">
    <div class="modal-content">
      <div class="modal-header">
        <h2>
          Edit {{ addressType === "shipping" ? "Shipping" : "Billing" }} Address
        </h2>
        <button
          type="button"
          @click="handleCancel"
          class="close-button"
          :disabled="isSaving"
        >
          ×
        </button>
      </div>

      <form @submit.prevent="handleSave" class="modal-form">
        <button
          v-if="addressType === 'billing' && shippingAddress"
          type="button"
          class="copy-shipping-button"
          @click="copyFromShipping"
        >
          Copy from Shipping
        </button>
        <div class="form-grid">
          <div class="form-group">
            <label>Name</label>
            <input v-model="formData.name" type="text" />
          </div>

          <div class="form-group">
            <label>Company</label>
            <input v-model="formData.company" type="text" />
          </div>

          <div class="form-group">
            <label>Street 1</label>
            <input v-model="formData.street1" type="text" />
          </div>

          <div class="form-group">
            <label>Street 2</label>
            <input v-model="formData.street2" type="text" />
          </div>

          <div class="form-group full-width">
            <label>City</label>
            <input v-model="formData.city" type="text" />
          </div>

          <!-- State with autocomplete -->
          <div class="form-group autocomplete-group">
            <label>State</label>
            <div class="autocomplete-wrapper">
              <input
                v-model="formData.state"
                type="text"
                @input="onStateInput"
                @focus="showStateDropdown = true"
                @blur="delayHideStateDropdown"
                autocomplete="off"
                :class="{
                  'validation-warning': stateValidation === 'warning',
                  'validation-valid': stateValidation === 'valid'
                }"
              />
              <span v-if="isLoadingStates" class="field-loader">⏳</span>
              <ul
                v-if="showStateDropdown && filteredStates.length > 0"
                class="autocomplete-dropdown"
              >
                <li
                  v-for="state in filteredStates"
                  :key="state.estadoId"
                  @mousedown.prevent="selectState(state)"
                >
                  <span class="option-name">{{ state.nombreEstado }}</span>
                  <span class="option-code">{{ state.codEstado }}</span>
                </li>
              </ul>
            </div>
            <span
              v-if="stateValidation === 'warning'"
              class="validation-msg warning"
            >
              State not found in Infoauto
            </span>
            <span
              v-else-if="stateValidation === 'valid'"
              class="validation-msg valid"
            >
              ✓ Valid
            </span>
          </div>

          <div class="form-group">
            <label>Postal Code</label>
            <input v-model="formData.postalCode" type="text" />
          </div>

          <!-- Country with autocomplete -->
          <div class="form-group full-width autocomplete-group">
            <label>Country</label>
            <div class="autocomplete-wrapper">
              <input
                v-model="formData.country"
                type="text"
                @input="onCountryInput"
                @focus="onCountryFocus"
                @blur="delayHideCountryDropdown"
                autocomplete="off"
                :class="{
                  'validation-warning': countryValidation === 'warning',
                  'validation-valid': countryValidation === 'valid'
                }"
              />
              <span v-if="isLoadingCountries" class="field-loader">⏳</span>
              <ul
                v-if="showCountryDropdown && filteredCountries.length > 0"
                class="autocomplete-dropdown"
              >
                <li
                  v-for="country in filteredCountries"
                  :key="country.paisId"
                  @mousedown.prevent="selectCountry(country)"
                >
                  <span class="option-name">{{ country.nombrePais }}</span>
                  <span class="option-code">{{ country.codPais }}</span>
                </li>
              </ul>
            </div>
            <span
              v-if="countryValidation === 'warning'"
              class="validation-msg warning"
            >
              Country not found in Infoauto
            </span>
            <span
              v-else-if="countryValidation === 'valid'"
              class="validation-msg valid"
            >
              ✓ Valid
            </span>
          </div>

          <div class="form-group full-width">
            <label>Phone</label>
            <input v-model="formData.phone" type="text" />
          </div>

          <div class="form-group checkbox full-width">
            <label>
              <input v-model="formData.residential" type="checkbox" />
              Residential
            </label>
          </div>

          <div class="form-group checkbox full-width">
            <label>
              <input v-model="formData.address_verified" type="checkbox" />
              Address Verified
            </label>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            @click="handleCancel"
            class="cancel-button"
            :disabled="isSaving"
          >
            Cancel
          </button>
          <button type="submit" class="save-button" :disabled="isSaving">
            {{ isSaving ? "Saving..." : "Save Address" }}
          </button>
        </div>
      </form>
    </div>
  </BaseModal>
</template>

<script>
import { lookupCountries, lookupStates } from "@/api/Infoauto.js";

export default {
  props: {
    value: {
      type: Boolean,
      required: true
    },
    address: {
      type: Object,
      required: true
    },
    addressType: {
      type: String,
      required: true,
      validator(value) {
        return ["shipping", "billing"].includes(value);
      }
    },
    isSaving: {
      type: Boolean,
      default: false
    },
    shippingAddress: {
      type: Object,
      default: null
    }
  },
  mounted() {
    window.addEventListener("keydown", this.interceptEscape, true);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.interceptEscape, true);
  },
  data() {
    return {
      formData: JSON.parse(JSON.stringify(this.address)),
      // Countries
      countries: [],
      isLoadingCountries: false,
      showCountryDropdown: false,
      selectedCountry: null,
      countryValidation: null, // null | 'valid' | 'warning'
      // States
      states: [],
      isLoadingStates: false,
      showStateDropdown: false,
      selectedState: null,
      stateValidation: null,
      // Debounce
      stateSearchTimeout: null
    };
  },
  computed: {
    isOpen: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    },
    filteredCountries() {
      if (!this.formData.country || this.formData.country.trim() === "") {
        return this.countries;
      }
      const search = this.formData.country.toLowerCase();
      return this.countries.filter(
        c =>
          c.nombrePais.toLowerCase().includes(search) ||
          c.codPais.toLowerCase().includes(search)
      );
    },
    filteredStates() {
      return this.states;
    }
  },
  watch: {
    address: {
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.formData = JSON.parse(JSON.stringify(newVal));
        }
      },
      deep: true
    },
    value(val) {
      if (val) {
        this.formData = JSON.parse(JSON.stringify(this.address));
        this.countryValidation = null;
        this.stateValidation = null;
        this.selectedCountry = null;
        this.selectedState = null;
        this.loadCountries();
      }
    }
  },
  methods: {
    handleSave() {
      const payload = { ...this.formData };
      if (this.selectedCountry && this.selectedCountry.codPais) {
        payload.country = this.selectedCountry.codPais;
      }
      this.$emit("save", payload);
    },
    handleCancel() {
      this.$emit("input", false);
    },
    copyFromShipping() {
      if (!this.shippingAddress) return;
      const src = this.shippingAddress;
      this.formData = {
        ...this.formData,
        name: src.name || "",
        company: src.company || "",
        street1: src.street1 || "",
        street2: src.street2 || "",
        street3: src.street3 || "",
        city: src.city || "",
        state: src.state || "",
        postalCode: src.postalCode || "",
        postal_code: src.postalCode || "",
        country: src.country || "",
        phone: src.phone || "",
        residential: src.residential ? 1 : 0,
        address_verified: src.address_verified ? 1 : 0
      };
      this.selectedCountry = null;
      this.selectedState = null;
      this.countryValidation = null;
      this.stateValidation = null;
      this.tryMatchExistingCountry();
    },
    interceptEscape(event) {
      if (event.key === "Escape" && this.value) {
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    },

    // --- Countries ---
    async loadCountries() {
      if (this.countries.length > 0) {
        this.tryMatchExistingCountry();
        return;
      }
      this.isLoadingCountries = true;
      try {
        const data = await lookupCountries();
        this.countries = Array.isArray(data) ? data : [];
        this.tryMatchExistingCountry();
      } catch (e) {
        console.error("Failed to load countries", e);
        this.countries = [];
      } finally {
        this.isLoadingCountries = false;
      }
    },
    tryMatchExistingCountry() {
      if (!this.formData.country) return;
      const search = this.formData.country.toLowerCase().trim();
      const match = this.countries.find(
        c =>
          c.nombrePais.toLowerCase() === search ||
          c.codPais.toLowerCase() === search
      );
      if (match) {
        this.selectedCountry = match;
        this.countryValidation = "valid";
        this.loadStatesForCountry(match.codPais);
      } else {
        this.countryValidation = search.length > 0 ? "warning" : null;
      }
    },
    onCountryFocus() {
      this.showCountryDropdown = true;
      if (this.countries.length === 0) {
        this.loadCountries();
      }
    },
    onCountryInput() {
      this.showCountryDropdown = true;
      this.selectedCountry = null;
      this.countryValidation = null;
      // Reset state when country changes
      this.states = [];
      this.selectedState = null;
      this.stateValidation = null;
      // Try to auto-match
      this.tryMatchExistingCountry();
    },
    selectCountry(country) {
      this.formData.country = country.nombrePais;
      this.selectedCountry = country;
      this.countryValidation = "valid";
      this.showCountryDropdown = false;
      // Reset state and load states for new country
      this.formData.state = "";
      this.states = [];
      this.selectedState = null;
      this.stateValidation = null;
      this.loadStatesForCountry(country.codPais);
    },
    delayHideCountryDropdown() {
      setTimeout(() => {
        this.showCountryDropdown = false;
        if (!this.selectedCountry && this.formData.country) {
          this.tryMatchExistingCountry();
        }
      }, 200);
    },

    // --- States ---
    async loadStatesForCountry(countryCode, stateName = null) {
      this.isLoadingStates = true;
      try {
        const data = await lookupStates(countryCode, stateName);
        this.states = Array.isArray(data) ? data : [];
        this.tryMatchExistingState();
      } catch (e) {
        console.error("Failed to load states", e);
        this.states = [];
      } finally {
        this.isLoadingStates = false;
      }
    },
    tryMatchExistingState() {
      if (!this.formData.state) return;
      const search = this.formData.state.toLowerCase().trim();
      const match = this.states.find(
        s =>
          s.nombreEstado.toLowerCase() === search ||
          s.codEstado.toLowerCase() === search
      );
      if (match) {
        this.selectedState = match;
        this.stateValidation = "valid";
      } else {
        this.stateValidation = search.length > 0 ? "warning" : null;
      }
    },
    onStateInput() {
      this.showStateDropdown = true;
      this.selectedState = null;
      this.stateValidation = null;

      if (!this.selectedCountry) return;

      // Debounce the API call for state search
      clearTimeout(this.stateSearchTimeout);
      this.stateSearchTimeout = setTimeout(() => {
        if (this.formData.state && this.formData.state.trim().length >= 2) {
          this.loadStatesForCountry(
            this.selectedCountry.codPais,
            this.formData.state.trim()
          );
        } else if (!this.formData.state || this.formData.state.trim() === "") {
          this.loadStatesForCountry(this.selectedCountry.codPais);
        }
      }, 300);
    },
    selectState(state) {
      this.formData.state = state.codEstado || state.nombreEstado;
      this.selectedState = state;
      this.stateValidation = "valid";
      this.showStateDropdown = false;
    },
    delayHideStateDropdown() {
      setTimeout(() => {
        this.showStateDropdown = false;
        if (!this.selectedState && this.formData.state) {
          this.tryMatchExistingState();
        }
      }, 200);
    }
  }
};
</script>

<style scoped>
:deep(.address-modal .modal-content) {
  width: 90%;
  max-width: 600px;
  height: auto;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background-color: #333;
  color: white;
  border-bottom: 1px solid #ddd;
}

.modal-header h2 {
  margin: 0;
  font-size: 20px;
}

.close-button {
  background: none;
  border: none;
  font-size: 28px;
  color: white;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
}

.close-button:hover:not(:disabled) {
  opacity: 0.7;
}

.close-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.modal-form {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group.checkbox {
  grid-column: 1 / -1;
  flex-direction: row;
  align-items: center;
}

.form-group label {
  font-weight: 500;
  margin-bottom: 6px;
  font-size: 14px;
  color: #333;
}

.form-group.checkbox label {
  margin-bottom: 0;
  margin-left: 8px;
  display: flex;
  align-items: center;
  font-weight: normal;
}

.form-group input[type="text"] {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-group input[type="text"]:focus {
  outline: none;
  border-color: #4a90e2;
  box-shadow: 0 0 5px rgba(74, 144, 226, 0.3);
}

.form-group input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px;
  background-color: #f5f5f5;
  border-top: 1px solid #ddd;
}

.copy-shipping-button {
  display: inline-block;
  margin-bottom: 16px;
  padding: 8px 16px;
  border: 1px solid #007bff;
  border-radius: 4px;
  background-color: white;
  color: #007bff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.copy-shipping-button:hover {
  background-color: #007bff;
  color: white;
}

.save-button,
.cancel-button {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.save-button {
  background-color: #28a745;
  color: white;
}

.save-button:hover:not(:disabled) {
  background-color: #218838;
}

.save-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.cancel-button {
  background-color: #6c757d;
  color: white;
}

.cancel-button:hover:not(:disabled) {
  background-color: #5a6268;
}

.cancel-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

/* Autocomplete styles */
.autocomplete-group {
  position: relative;
}

.autocomplete-wrapper {
  position: relative;
}

.field-loader {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
}

.autocomplete-dropdown {
  position: absolute;
  z-index: 100;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  list-style: none;
  padding: 0;
  margin: 0;
}

.autocomplete-dropdown li {
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  border-bottom: 1px solid #f0f0f0;
}

.autocomplete-dropdown li:last-child {
  border-bottom: none;
}

.autocomplete-dropdown li:hover {
  background-color: #e8f0fe;
}

.option-name {
  flex: 1;
}

.option-code {
  color: #666;
  font-size: 12px;
  font-weight: 600;
  margin-left: 8px;
  background: #f0f0f0;
  padding: 2px 6px;
  border-radius: 3px;
}

.validation-warning {
  border-color: #ffc107 !important;
  background-color: #fffdf0;
}

.validation-valid {
  border-color: #28a745 !important;
}

.validation-msg {
  font-size: 12px;
  margin-top: 4px;
}

.validation-msg.warning {
  color: #d68000;
}

.validation-msg.valid {
  color: #28a745;
}
</style>
